import { supabase } from './supabase';
import { venuePhotoStoragePath } from './cropImage';

/**
 * Venue-photo write operations for the Photo Approval screen.
 *
 * ⚠️ DELIBERATE DUPLICATE — READ BEFORE EDITING ⚠️
 * `approveVenuePhoto` below is a line-for-line port of `VenueReviewScreen.approvePhoto`
 * (`src/pages/VenueReviewScreen.tsx:954-1063`), and `rejectVenuePhoto` of `rejectPhoto`
 * (`:1082-1095`). It is NOT shared code: the Venues page was explicitly frozen on 2026-08-02
 * ("the venue photo approval process that I currently do in the venues page doesn't change how
 * it works"), and extracting the logic would have meant editing that file. Duplicating was the
 * only way to leave it byte-identical.
 *
 * Consequence: these two implementations must be changed together. If you touch the approval
 * sequence here, mirror it there (and vice-versa) — they write the same rows.
 *
 * The sequence, and why each step is in that order:
 *  1. Read the venue's current primary + Google thumbnail.
 *  2. `hasExistingPrimary` — an approval must NEVER displace a primary that is already locked in.
 *     Only the first-ever approval (or an explicit "Make primary") sets
 *     `venues.approved_venue_photo_url`.
 *  3. Normalise the venue's existing approved photos to explicit `display_order` 0..n-1 (legacy
 *     rows carry null) and append this one at n — the true end of the carousel.
 *  4. `admin_set_approved_venue_photo` RPC, not a direct UPDATE: RLS policy `venues_update_logger`
 *     blocks admins from updating `venues` directly. The RPC is SECURITY DEFINER and also clears
 *     `google_photo_r2_url` + sets `google_fetch_disabled`.
 *  5/6. Best-effort storage cleanup — non-blocking, because the row state is already correct.
 */

export type ApprovableRow = {
  id: string;
  venue_id: string;
  /** The row's current `public_url` — the flash/venue-direct URL while still pending. */
  public_url: string | null;
};

/** Everything needed to put the DB back exactly as it was. See {@link undoApproveVenuePhoto}. */
export type ApproveSnapshot = {
  photoId: string;
  venueId: string;
  previousPublicUrl: string | null;
  previousDisplayOrder: number | null;
  /** display_order values overwritten by the 0..n-1 normalisation, so undo can put them back. */
  renumbered: { id: string; previousOrder: number | null }[];
  /** This approval set `venues.approved_venue_photo_url`; undo must clear it back. */
  becamePrimary: boolean;
  previousPrimaryUrl: string | null;
  /**
   * The Google thumbnail object deleted from Storage as part of this approval. Undo restores the
   * DB but CANNOT bring this file back — surface that wherever undo is offered.
   */
  deletedGoogleObjectPath: string | null;
};

export type ApproveResult = {
  becamePrimary: boolean;
  snapshot: ApproveSnapshot;
};

/**
 * Approve one `venue_photos` row. `approvedPublicUrl` is the cropped file when the admin composed
 * a crop; omit it to approve the submitted image untouched (the common case now that raters
 * upload square).
 */
export async function approveVenuePhoto(
  photo: ApprovableRow,
  adminUserId: string,
  approvedPublicUrl?: string,
): Promise<ApproveResult> {
  const url = (approvedPublicUrl ?? photo.public_url ?? '').trim();
  if (!url || !photo.venue_id) throw new Error('Photo has no URL or venue');

  const { data: venueRow, error: readErr } = await supabase
    .from('venues')
    .select('id, google_photo_r2_url, approved_venue_photo_url')
    .eq('id', photo.venue_id)
    .maybeSingle();
  if (readErr) throw readErr;

  const oldGoogleUrl = (venueRow?.google_photo_r2_url ?? null) as string | null;
  const previousPrimaryUrl = ((venueRow?.approved_venue_photo_url ?? null) as string | null) || null;
  const hasExistingPrimary = previousPrimaryUrl != null;

  const { data: existingApproved, error: exErr } = await supabase
    .from('venue_photos')
    .select('id, display_order')
    .eq('venue_id', photo.venue_id)
    .eq('approved', true)
    .order('display_order', { ascending: true, nullsFirst: false })
    .order('approved_at', { ascending: false });
  if (exErr) throw exErr;

  const existingOrdered = (existingApproved ?? []) as { id: string; display_order: number | null }[];
  const renumbers = existingOrdered
    .map((p, idx) => ({ id: p.id, order: idx, previousOrder: p.display_order, changed: p.display_order !== idx }))
    .filter((p) => p.changed);
  const nextOrder = existingOrdered.length;

  const { data: selfRow } = await supabase
    .from('venue_photos')
    .select('display_order')
    .eq('id', photo.id)
    .maybeSingle();

  const now = new Date().toISOString();
  const results = await Promise.all([
    supabase
      .from('venue_photos')
      .update({ approved: true, approved_by: adminUserId, approved_at: now, public_url: url, display_order: nextOrder })
      .eq('id', photo.id),
    ...renumbers.map((r) => supabase.from('venue_photos').update({ display_order: r.order }).eq('id', r.id)),
  ]);
  const firstUpdateErr = results.find((r) => r.error)?.error;
  if (firstUpdateErr) throw firstUpdateErr;

  if (!hasExistingPrimary) {
    const { error: e2 } = await supabase.rpc('admin_set_approved_venue_photo', {
      p_venue_id: photo.venue_id,
      p_url: url,
    });
    if (e2) throw e2;
  }

  let deletedGoogleObjectPath: string | null = null;
  if (!hasExistingPrimary && oldGoogleUrl) {
    try {
      const marker = '/venue-photos/';
      const idx = oldGoogleUrl.indexOf(marker);
      if (idx >= 0) {
        const path = oldGoogleUrl.slice(idx + marker.length);
        await supabase.storage.from('venue-photos').remove([path]);
        deletedGoogleObjectPath = path;
      }
    } catch {
      /* non-blocking */
    }
  }

  if (approvedPublicUrl && photo.public_url) {
    try {
      const oldPath = venuePhotoStoragePath(photo.public_url);
      const newPath = venuePhotoStoragePath(url);
      // No-op for a flashes URL (venuePhotoStoragePath only resolves /venue-photos/) — which is
      // exactly why rating originals survive approval and can be restored later.
      if (oldPath && oldPath !== newPath) {
        await supabase.storage.from('venue-photos').remove([oldPath]);
      }
    } catch {
      /* non-blocking */
    }
  }

  return {
    becamePrimary: !hasExistingPrimary,
    snapshot: {
      photoId: photo.id,
      venueId: photo.venue_id,
      previousPublicUrl: photo.public_url ?? null,
      previousDisplayOrder: (selfRow?.display_order ?? null) as number | null,
      renumbered: renumbers.map((r) => ({ id: r.id, previousOrder: r.previousOrder })),
      becamePrimary: !hasExistingPrimary,
      previousPrimaryUrl,
      deletedGoogleObjectPath,
    },
  };
}

/**
 * Reverse an approval. Restores every row/column this dashboard changed.
 *
 * NOT restored: a Google thumbnail deleted in step 5 (the object is gone from Storage), and a
 * cropped file uploaded before approval (left orphaned in `admin-approved/`, harmless).
 */
export async function undoApproveVenuePhoto(s: ApproveSnapshot): Promise<void> {
  const { error } = await supabase
    .from('venue_photos')
    .update({
      approved: false,
      approved_by: null,
      approved_at: null,
      public_url: s.previousPublicUrl,
      display_order: s.previousDisplayOrder,
    })
    .eq('id', s.photoId);
  if (error) throw error;

  const results = await Promise.all(
    s.renumbered.map((r) => supabase.from('venue_photos').update({ display_order: r.previousOrder }).eq('id', r.id)),
  );
  const firstErr = results.find((r) => r.error)?.error;
  if (firstErr) throw firstErr;

  if (s.becamePrimary) {
    const { error: e2 } = await supabase.rpc('admin_set_approved_venue_photo', {
      p_venue_id: s.venueId,
      p_url: s.previousPrimaryUrl,
    });
    if (e2) throw e2;
  }
}

/** Port of `VenueReviewScreen.rejectPhoto` (`:1082-1095`) — a flag flip, nothing is destroyed. */
export async function rejectVenuePhoto(photoId: string): Promise<void> {
  const { data, error } = await supabase
    .from('venue_photos')
    .update({ rejected: true })
    .eq('id', photoId)
    .select('id');
  if (error) throw error;
  /* returning the row surfaces the silent failure mode: RLS filtering the update to 0 rows
     reports no error at all — it just does nothing (2026-09-22 debugging) */
  if (!data || data.length === 0) throw new Error('Reject wrote 0 rows — the row is not visible/updatable under RLS, or was already gone');
}

export async function undoRejectVenuePhoto(photoId: string): Promise<void> {
  const { error } = await supabase.from('venue_photos').update({ rejected: false }).eq('id', photoId);
  if (error) throw error;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Restore-to-original                                                        */
/* ────────────────────────────────────────────────────────────────────────── */

export type RestoreWrite = {
  photoId: string;
  venueId: string;
  targetUrl: string;
  /** Row is `venues.approved_venue_photo_url` — the venue pointer moves too. */
  isHero: boolean;
  /** Pre-write value, so the whole batch can be rolled back. */
  previousUrl: string;
};

export type RestoreOutcome = { write: RestoreWrite; ok: boolean; error?: string };

/**
 * Point one approved photo back at the rater's original upload.
 *
 * NO image processing and NO storage writes — the original is already square, so this is a pure
 * pointer change: lossless, instant, and reversible (the cropped `admin-approved/` file is left
 * in place on purpose, which is what makes {@link undoRestoreOriginals} possible).
 *
 * Order matters: `venue_photos.public_url` first. If the second call fails, the carousel and the
 * hero disagree but nothing is lost; if it were reversed, a failure would leave the venue pointing
 * at a URL no `venue_photos` row claims — the invariant the whole screen navigates by.
 */
export async function restoreOriginal(write: RestoreWrite): Promise<void> {
  const { error } = await supabase
    .from('venue_photos')
    .update({ public_url: write.targetUrl })
    .eq('id', write.photoId);
  if (error) throw error;

  if (write.isHero) {
    const { error: e2 } = await supabase.rpc('admin_set_approved_venue_photo', {
      p_venue_id: write.venueId,
      p_url: write.targetUrl,
    });
    if (e2) throw e2;
  }
}

/** Sequential on purpose: a partial failure must leave a readable, reportable trail. */
export async function restoreOriginals(
  writes: RestoreWrite[],
  onProgress?: (done: number, total: number) => void,
): Promise<RestoreOutcome[]> {
  const out: RestoreOutcome[] = [];
  for (let i = 0; i < writes.length; i++) {
    const write = writes[i];
    try {
      await restoreOriginal(write);
      out.push({ write, ok: true });
    } catch (e: unknown) {
      out.push({ write, ok: false, error: e instanceof Error ? e.message : 'Restore failed' });
    }
    onProgress?.(i + 1, writes.length);
  }
  return out;
}

/** Put a completed restore batch back on the cropped files (the objects were never deleted). */
export async function undoRestoreOriginals(writes: RestoreWrite[]): Promise<RestoreOutcome[]> {
  return restoreOriginals(
    writes.map((w) => ({ ...w, targetUrl: w.previousUrl, previousUrl: w.targetUrl })),
  );
}

/** Port of `VenueReviewScreen.makePrimaryPhoto`'s venue write (`:1170-1201`), without reordering. */
export async function setVenuePrimaryPhoto(venueId: string, url: string | null): Promise<void> {
  const { error } = await supabase.rpc('admin_set_approved_venue_photo', { p_venue_id: venueId, p_url: url });
  if (error) throw error;
}
